import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import RecipientMultiSelect from '../components/RecipientMultiSelect';
import RecipientSummary from '../components/RecipientSummary';
import JournalService from '../services/JournalService';
import MessageService from '../services/MessageService';
import ExternalMessageService from '../services/ExternalMessageService';
import PointsService from '../services/PointsService';
import StaffService from '../services/StaffService';
import ProgrammeService from '../services/ProgrammeService';
import { useStudentPortal } from '../store/StudentPortalContext';
import { useSettings } from '../store/SettingsContext';
import { formatDateTime } from '../utils/dateFormat';

const categories = [
  { id: 'wellbeing', label: 'Wellbeing check-in' },
  { id: 'note', label: 'Note' },
  { id: 'support', label: 'Seeking help' },
  { id: 'job-coach', label: 'Job coach' },
  { id: 'safeguarding', label: 'Safeguarding' },
  { id: 'course-notes', label: 'Course notes' },
  { id: 'query', label: 'Question' }
];

const buildFileMeta = (file) => ({
  name: file.name,
  type: file.type || 'file',
  size: `${Math.round(file.size / 1024)} KB`,
  fileUrl: ''
});

const readFileAsDataUrl = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve({ ...buildFileMeta(file), fileUrl: reader.result?.toString() || '' });
    };
    reader.onerror = () => {
      resolve({ ...buildFileMeta(file), fileUrl: '' });
    };
    reader.readAsDataURL(file);
  });

const readFiles = async (fileList) => {
  const files = Array.from(fileList || []);
  if (files.length === 0) return [];
  return Promise.all(files.map((file) => readFileAsDataUrl(file)));
};

const buildRecipientMap = (staffList = []) => {
  const map = new Map();
  staffList.forEach((staff) => {
    map.set(staff.id, staff);
    map.set(staff.name, staff);
  });
  return map;
};

export default function StudentPortalJournalPage() {
  const { student } = useStudentPortal();
  const { settings } = useSettings();
  const location = useLocation();
  const portalSettings = settings?.studentPortal || {};
  const allowNoRecipients = Boolean(portalSettings.allowPrivateJournal);
  const journalService = useMemo(() => new JournalService(), []);
  const messageService = useMemo(() => new MessageService(), []);
  const externalMessageService = useMemo(() => new ExternalMessageService(), []);
  const pointsService = useMemo(() => new PointsService(), []);
  const staffService = useMemo(() => new StaffService(), []);
  const programmeService = useMemo(() => new ProgrammeService(), []);
  const staffList = useMemo(() => staffService.list(), [staffService]);
  const programmes = useMemo(() => programmeService.list(), [programmeService]);
  const recipientMap = useMemo(() => buildRecipientMap(staffList), [staffList]);
  const [category, setCategory] = useState('wellbeing');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState([]);
  const [status, setStatus] = useState('');
  const [errors, setErrors] = useState([]);
  const [prefilled, setPrefilled] = useState(false);
  const [, setRefreshTick] = useState(0);
  const [messageTick, setMessageTick] = useState(0);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyBody, setReplyBody] = useState('');
  const [replyStatus, setReplyStatus] = useState('');
  const [draftStatus, setDraftStatus] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const journalEntries = student ? journalService.listForStudent(student.id) : [];

  const staffReplies = useMemo(() => {
    void messageTick;
    if (!student) return [];
    return externalMessageService
      .listForStudent(student.id)
      .filter((message) => message.fromType === 'staff');
  }, [externalMessageService, student, messageTick]);

  const repliesByEntry = useMemo(() => {
    const map = new Map();
    staffReplies.forEach((message) => {
      const refs = (message.references || []).filter((ref) => ref.type === 'journal');
      refs.forEach((ref) => {
        const list = map.get(ref.id) || [];
        list.push(message);
        map.set(ref.id, list);
      });
    });
    return map;
  }, [staffReplies]);

  const supportTeam = useMemo(() => {
    if (!student) return [];
    const results = [];
    const addRecipient = (staffRef, roleLabel, groupLabel) => {
      if (!staffRef) return;
      const staffRecord =
        (staffRef.id && recipientMap.get(staffRef.id)) ||
        (staffRef.name && recipientMap.get(staffRef.name));
      const id = staffRecord?.id || staffRef.id || staffRef.name;
      if (!id) return;
      if (results.some((entry) => entry.id === id)) return;
      results.push({
        id,
        name: staffRecord?.name || staffRef.name || 'Staff',
        meta: roleLabel,
        group: groupLabel || roleLabel,
        email: staffRecord?.email || staffRef.email || '',
        phone: staffRecord?.phone || staffRef.phone || ''
      });
    };
    addRecipient(student.keyWorker, 'Key worker', 'Key worker');
    (student.supportWorkers || []).forEach((worker) => addRecipient(worker, 'Support worker', 'Support workers'));
    const programmeNames = new Set((student.enrolments || []).map((enrol) => enrol.programme));
    programmes
      .filter((programme) => programmeNames.has(programme.name))
      .map((programme) => programme.staff)
      .filter(Boolean)
      .forEach((staffName) => addRecipient({ name: staffName }, 'Programme staff', 'Programme staff'));
    return results;
  }, [student, programmes, recipientMap]);

  useEffect(() => {
    if (supportTeam.length && selectedRecipientIds.length === 0) {
      const keyWorker = supportTeam.find((member) => member.meta === 'Key worker');
      if (keyWorker) {
        setSelectedRecipientIds([keyWorker.id]);
      } else {
        setSelectedRecipientIds([supportTeam[0].id]);
      }
    }
  }, [supportTeam, selectedRecipientIds.length]);

  useEffect(() => {
    if (prefilled) return;
    if (location.state?.category) {
      setCategory(location.state.category);
    }
    if (location.state?.title) {
      setTitle(location.state.title);
    }
    if (location.state?.body) {
      setBody(location.state.body);
    }
    if (Array.isArray(location.state?.recipients)) {
      setSelectedRecipientIds(location.state.recipients);
    }
    if (location.state?.focus) {
      setTimeout(() => {
        const el = document.getElementById('journal-message');
        if (el) el.focus();
      }, 0);
    }
    setPrefilled(true);
  }, [location.state, prefilled]);

  const draftKey = student ? `safeguardpro_journal_draft_${student.id}` : '';

  useEffect(() => {
    if (!student || !draftKey) return;
    if (prefilled) return;
    try {
      const raw = window.localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed.category) setCategory(parsed.category);
      if (parsed.title) setTitle(parsed.title);
      if (parsed.body) setBody(parsed.body);
      if (Array.isArray(parsed.selectedRecipientIds) && parsed.selectedRecipientIds.length) {
        setSelectedRecipientIds(parsed.selectedRecipientIds);
      }
      if (Array.isArray(parsed.attachments)) {
        setAttachments(parsed.attachments);
      }
    } catch (error) {
      // ignore draft load errors
    }
  }, [student, draftKey, prefilled]);

  useEffect(() => {
    if (!student || !draftKey) return;
    const timeout = window.setTimeout(() => {
      const payload = {
        category,
        title,
        body,
        selectedRecipientIds,
        attachments: attachments.map((file) => ({
          name: file.name,
          type: file.type,
          size: file.size
        }))
      };
      window.localStorage.setItem(draftKey, JSON.stringify(payload));
      setDraftStatus('Draft saved');
    }, 600);
    return () => window.clearTimeout(timeout);
  }, [student, draftKey, category, title, body, selectedRecipientIds, attachments]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setMessageTick((prev) => prev + 1);
    }, 10000);
    return () => window.clearInterval(intervalId);
  }, []);

  const lastWellbeingEntry = journalEntries
    .filter((entry) => entry.category === 'wellbeing')
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
  const needsCheckin = lastWellbeingEntry
    ? new Date(lastWellbeingEntry.createdAt) < new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    : true;

  const selectedRecipients = selectedRecipientIds
    .map((id) => supportTeam.find((member) => member.id === id))
    .filter(Boolean)
    .map((member) => ({ id: member.id, name: member.name, type: 'staff' }));

  const keyWorker = supportTeam.find((member) => member.meta === 'Key worker');
  const supportTeamIds = supportTeam.map((member) => member.id);
  const quickPicks = [
    keyWorker
      ? {
          id: 'key-worker',
          label: 'Key worker',
          onSelect: () => setSelectedRecipientIds([keyWorker.id])
        }
      : null,
    {
      id: 'support-team',
      label: 'Support team',
      onSelect: () => setSelectedRecipientIds(supportTeamIds)
    },
    {
      id: 'all-assigned',
      label: 'All assigned staff',
      onSelect: () => setSelectedRecipientIds(supportTeamIds)
    },
    allowNoRecipients
      ? {
          id: 'share-none',
          label: 'Share with nobody',
          onSelect: () => setSelectedRecipientIds([])
        }
      : null
  ].filter(Boolean);

  const privacyWarning =
    selectedRecipientIds.length > 1
      ? 'You are sharing with multiple people. Only share what you are comfortable with.'
      : '';

  const handleRecipientChange = (next) => {
    setSelectedRecipientIds(next);
    if (errors.length) setErrors([]);
  };

  const resolveStaffRecipient = (message) => {
    if (!message) return null;
    const match = supportTeam.find((member) => member.id === message.fromId || member.name === message.fromName);
    if (match) return { id: match.id, name: match.name };
    if (message.fromId || message.fromName) {
      return { id: message.fromId || message.fromName, name: message.fromName || 'Staff' };
    }
    return null;
  };

  const resolveStaffFromEntry = (entry) => {
    if (!entry) return null;
    const match = supportTeam.find((member) => member.id === entry.authorId || member.name === entry.authorName);
    if (match) return { id: match.id, name: match.name };
    if (entry.authorId || entry.authorName) {
      return { id: entry.authorId || entry.authorName, name: entry.authorName || 'Support team' };
    }
    return null;
  };

  const handleReplyToEntry = (entry) => {
    if (!student) return;
    if (!replyBody.trim()) {
      setReplyStatus('Write a reply before sending.');
      return;
    }
    const recipient = resolveStaffFromEntry(entry);
    if (!recipient) {
      setReplyStatus('No staff recipient found for this reply.');
      return;
    }
    externalMessageService.logStudentMessage({
      subject: entry.title ? `Re: ${entry.title}` : 'Re: Support update',
      body: replyBody.trim(),
      student,
      staffRecipients: [recipient],
      category: 'journal-reply',
      references: [{ type: 'journal', id: entry.id, label: `${student.name} journal entry` }]
    });
    setReplyStatus('Reply sent to your support team.');
    setReplyBody('');
    setReplyingTo(null);
    setMessageTick((prev) => prev + 1);
  };

  const handleSaveDraft = () => {
    if (!student || !draftKey) return;
    const payload = {
      category,
      title,
      body,
      selectedRecipientIds,
      attachments: attachments.map((file) => ({
        name: file.name,
        type: file.type,
        size: file.size
      }))
    };
    window.localStorage.setItem(draftKey, JSON.stringify(payload));
    setDraftStatus('Draft saved');
  };

  const handleCancelDraft = () => {
    setTitle('');
    setBody('');
    setAttachments([]);
    if (keyWorker) {
      setSelectedRecipientIds([keyWorker.id]);
    } else {
      setSelectedRecipientIds([]);
    }
    setErrors([]);
    setStatus('');
  };

  const handleFilesSelected = async (fileList) => {
    const files = await readFiles(fileList);
    if (files.length === 0) return;
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleReplySend = (entryId, message) => {
    if (!student) return;
    if (!replyBody.trim()) {
      setReplyStatus('Write a reply before sending.');
      return;
    }
    const recipient = resolveStaffRecipient(message);
    if (!recipient) {
      setReplyStatus('No staff recipient found for this reply.');
      return;
    }
    externalMessageService.logStudentMessage({
      subject: message?.subject?.startsWith('Re:') ? message.subject : `Re: ${message?.subject || 'Journal reply'}`,
      body: replyBody.trim(),
      student,
      staffRecipients: [recipient],
      category: 'journal-reply',
      references: [{ type: 'journal', id: entryId, label: `${student.name} journal entry` }]
    });
    setReplyStatus('Reply sent to your support team.');
    setReplyBody('');
    setReplyingTo(null);
    setMessageTick((prev) => prev + 1);
  };

  const handleSubmit = () => {
    if (!student) return;
    setStatus('');
    const nextErrors = [];
    if (!body.trim()) {
      nextErrors.push({ id: 'body', message: 'Journal message is required.' });
    }
    if (!allowNoRecipients && selectedRecipientIds.length === 0) {
      nextErrors.push({ id: 'recipients', message: 'Select at least one support staff member.' });
    }
    if (nextErrors.length > 0) {
      setErrors(nextErrors);
      const errorSummary = document.getElementById('journal-error-summary');
      if (errorSummary) errorSummary.focus();
      return;
    }
    setErrors([]);
    const selectedCategory = categories.find((item) => item.id === category);
    const entry = journalService.createEntry({
      student,
      title: title.trim(),
      body: body.trim(),
      category,
      attachments,
      staffRecipients: selectedRecipients
    });

    if (selectedRecipients.length > 0) {
      messageService.send({
        subject: `Journal entry: ${selectedCategory?.label || 'Update'} - ${student.name}`,
        body: `${student.name} shared a journal entry.\n\n${title ? `${title}\n` : ''}${body.trim()}`,
        fromUser: { id: student.id, name: student.name },
        recipients: selectedRecipients,
        references: [{ type: 'journal', id: entry.id, label: `${student.name} journal entry` }]
      });
    }

    if (portalSettings.pointsEnabled && category !== 'wellbeing') {
      const rules = portalSettings.pointsRules || {};
      const points = Number(rules.journalEntry || 0);
      if (points > 0) {
        pointsService.awardPoints({
          student,
          points,
          reason: 'Journal entry',
          source: 'journal',
          createdBy: student.name,
          thresholds: portalSettings.rewards || [],
          staffRecipients: selectedRecipients
        });
      }
    }

    setTitle('');
    setBody('');
    setAttachments([]);
    setStatus(category === 'wellbeing' ? 'Wellbeing check-in sent for approval.' : 'Journal entry shared.');
    if (draftKey) {
      window.localStorage.removeItem(draftKey);
      setDraftStatus('');
    }
    setRefreshTick((prev) => prev + 1);
  };

  if (!student) {
    return (
      <Card className="p-6">
        <p className="sp-muted">Sign in to view your journal.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Journal</h2>
        <p className="sp-muted">Share updates with your support team and keep track of your progress.</p>
      </div>

      <Card className={`p-5 ${needsCheckin ? 'sp-panel--critical' : ''}`}>
        <div className="flex items-center justify-between gap-2">
          <div>
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Wellbeing check-in</h3>
            <p className="text-sm sp-muted">
              Keep a short note on how you are feeling, and share it with your support team if needed.
            </p>
          </div>
          {needsCheckin && <Badge variant="warning">Due</Badge>}
        </div>
        {lastWellbeingEntry && (
          <div className="text-xs sp-muted mt-2">Last check-in {formatDateTime(lastWellbeingEntry.createdAt)}</div>
        )}
        <Button
          size="sm"
          variant="secondary"
          className="mt-3"
          onClick={() => {
            setCategory('wellbeing');
            const el = document.getElementById('journal-message');
            if (el) el.focus();
          }}
        >
          Start check-in
        </Button>
      </Card>

      <Card className="p-6 space-y-4">
        <div>
          <h3 className="sp-value" style={{ fontWeight: 600 }}>New entry</h3>
          <p className="sp-muted">Write your update and choose who should see it.</p>
        </div>
        {errors.length > 0 && (
          <div
            id="journal-error-summary"
            tabIndex="-1"
            className="sp-alert sp-alert--critical text-sm"
            role="alert"
          >
            <div className="sp-value" style={{ fontWeight: 600 }}>Please fix the following</div>
            <ul className="mt-2 list-disc pl-5">
              {errors.map((error) => (
                <li key={error.id}>{error.message}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-4">
          <div className="space-y-4">
            <div>
              <label className="sp-label">Journal message *</label>
              <textarea
                id="journal-message"
                className={`sp-textarea mt-2 w-full ${errors.find((error) => error.id === 'body') ? 'sp-input--error' : ''}`}
                rows={8}
                value={body}
                onChange={(event) => {
                  setBody(event.target.value);
                  if (errors.length) setErrors([]);
                }}
                aria-required="true"
              />
            </div>
            <div>
              <label className="sp-label">Attachments</label>
              <div
                className={`sp-dropzone mt-2 ${dragActive ? 'is-dragging' : ''}`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragActive(false);
                  handleFilesSelected(event.dataTransfer.files);
                }}
              >
                <div className="text-sm">Drag files here, or click to upload</div>
                <div className="text-xs sp-muted">PDF, images, audio, or video.</div>
                <input
                  type="file"
                  multiple
                  aria-label="Upload attachments"
                  onChange={(event) => handleFilesSelected(event.target.files)}
                />
              </div>
              {attachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {attachments.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="sp-panel sp-panel--muted p-2 flex items-center justify-between">
                      <div className="text-sm">{file.name}</div>
                      <Button size="sm" variant="ghost" onClick={() => removeAttachment(index)}>
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="sp-label">Category</label>
              <select
                className="sp-select mt-2 w-full"
                value={category}
                onChange={(event) => setCategory(event.target.value)}
              >
                {categories.map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="sp-label">Title (optional)</label>
              <input
                className="sp-input mt-2 w-full"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>
            <RecipientMultiSelect
              label={`Share with${allowNoRecipients ? '' : ' *'}`}
              options={supportTeam}
              selectedIds={selectedRecipientIds}
              onChange={handleRecipientChange}
              quickPicks={quickPicks}
              recommendedId={keyWorker?.id || ''}
              allowNone={allowNoRecipients}
              helperText="Choose who should see this entry."
              error={errors.find((error) => error.id === 'recipients')?.message || ''}
            />
            {privacyWarning && (
              <div className="sp-panel sp-panel--muted p-2 text-xs sp-muted">
                {privacyWarning}
              </div>
            )}
            <RecipientSummary
              recipients={supportTeam.filter((member) => selectedRecipientIds.includes(member.id))}
              error={errors.find((error) => error.id === 'recipients')?.message || ''}
            />
          </div>
        </div>
        {draftStatus && <div className="text-xs sp-muted">{draftStatus}</div>}
        {status && <div className="text-xs sp-muted">{status}</div>}
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" onClick={handleSubmit} disabled={!body.trim() || (!allowNoRecipients && selectedRecipientIds.length === 0)}>
            Share entry
          </Button>
          <Button size="sm" variant="secondary" onClick={handleSaveDraft}>
            Save draft
          </Button>
          <Button size="sm" variant="ghost" onClick={handleCancelDraft}>
            Cancel
          </Button>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div>
          <h3 className="sp-value" style={{ fontWeight: 600 }}>History</h3>
          <p className="sp-muted">Your recent entries and support team replies.</p>
        </div>
        {journalEntries.length === 0 ? (
          <div className="sp-panel sp-panel--muted p-3 text-sm">No journal entries yet.</div>
        ) : (
          <div className="space-y-2">
            {journalEntries
              .slice()
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .map((entry) => (
                <div key={entry.id} className="sp-panel sp-panel--muted p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="sp-value" style={{ fontWeight: 600 }}>
                      {entry.authorType === 'staff'
                        ? 'Support team update'
                        : categories.find((item) => item.id === entry.category)?.label || 'Journal entry'}
                    </div>
                    <div className="text-xs sp-muted">{formatDateTime(entry.createdAt)}</div>
                  </div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {entry.authorType === 'staff' && (
                      <Badge variant="info">From {entry.authorName || 'Support team'}</Badge>
                    )}
                    {entry.status === 'pending' && <Badge variant="warning">Awaiting approval</Badge>}
                    {entry.status === 'approved' && <Badge variant="success">Approved</Badge>}
                    {entry.status === 'shared' && <Badge variant="active">Shared</Badge>}
                  </div>
                  {entry.title && <div className="text-sm mt-2">{entry.title}</div>}
                  {entry.body && <div className="text-sm sp-muted mt-1">{entry.body}</div>}
                  {entry.attachments?.length > 0 && (
                    <div className="text-xs sp-muted mt-2">
                      {entry.attachments.map((file) => file.name).join(', ')}
                    </div>
                  )}
                  {repliesByEntry.has(entry.id) && (
                    <div className="sp-panel sp-panel--muted p-3 mt-3 space-y-2">
                      <div className="sp-label">Support team replies</div>
                      {repliesByEntry
                        .get(entry.id)
                        .slice()
                        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                        .map((reply) => (
                          <div key={reply.id} className="sp-panel sp-panel--muted p-2">
                            <div className="text-xs sp-muted">
                              {reply.fromName} · {formatDateTime(reply.createdAt)}
                            </div>
                            <div className="text-sm mt-1">{reply.body}</div>
                            <div className="mt-2 flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="secondary"
                                type="button"
                                onClick={() => {
                                  setReplyingTo({ entryId: entry.id, messageId: reply.id });
                                  setReplyBody('');
                                  setReplyStatus('');
                                }}
                              >
                                Reply
                              </Button>
                            </div>
                            {replyingTo?.messageId === reply.id && (
                              <div className="mt-3 space-y-2">
                                <textarea
                                  className="sp-textarea w-full"
                                  rows={3}
                                  value={replyBody}
                                  onChange={(event) => setReplyBody(event.target.value)}
                                  placeholder="Write your reply"
                                />
                                {replyStatus && <div className="text-xs sp-muted">{replyStatus}</div>}
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    type="button"
                                    onClick={() => handleReplySend(entry.id, reply)}
                                  >
                                    Send reply
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    type="button"
                                    onClick={() => {
                                      setReplyingTo(null);
                                      setReplyBody('');
                                      setReplyStatus('');
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                  {entry.authorType === 'staff' && (
                    <div className="mt-3 space-y-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          setReplyingTo({ entryId: entry.id, messageId: `entry-${entry.id}` });
                          setReplyBody('');
                          setReplyStatus('');
                        }}
                      >
                        Reply to support team
                      </Button>
                      {replyingTo?.messageId === `entry-${entry.id}` && (
                        <div className="mt-3 space-y-2">
                          <textarea
                            className="sp-textarea w-full"
                            rows={3}
                            value={replyBody}
                            onChange={(event) => setReplyBody(event.target.value)}
                            placeholder="Write your reply"
                          />
                          {replyStatus && <div className="text-xs sp-muted">{replyStatus}</div>}
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              type="button"
                              onClick={() => handleReplyToEntry(entry)}
                            >
                              Send reply
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              type="button"
                              onClick={() => {
                                setReplyingTo(null);
                                setReplyBody('');
                                setReplyStatus('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}
      </Card>
    </div>
  );
}
